import React, { useState, useEffect } from "react";
import { useData } from "../../../context/DataContext";
import { MultiImageUploadField } from "../../../utils/ImageUploadField";
import {
  confirmDialog,
  confirmDelete,
  successToast,
  errorToast,
} from "../../../utils/dialog";

const GalleryHero = () => {
  const {
    galleryHeroConfig,
    updateGalleryHeroConfig,
    uploadGalleryHeroBackground,
    deleteGalleryHeroImage,
  } = useData();

  const [localConfig, setLocalConfig] = useState({
    heroText: "",
    highlightText: "",
    backgroundUrls: [],
  });
  const [bgFiles, setBgFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  // Sync local state with context data
  useEffect(() => {
    setLocalConfig(galleryHeroConfig || {});
    setPreviewUrls(galleryHeroConfig?.backgroundUrls || []);
    setBgFiles([]);
    setSlideIndex(0);
  }, [galleryHeroConfig]);

  // Carousel auto-slide effect
  useEffect(() => {
    if (!previewUrls.length) return;
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % previewUrls.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [previewUrls]);

  // Handle text input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalConfig((prev) => ({ ...prev, [name]: value }));
  };

  // Add a new image for upload and preview
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgFiles((prev) => [...prev, file]);
    setPreviewUrls((prev) => [...prev, URL.createObjectURL(file)]);
    setSlideIndex(previewUrls.length);
    e.target.value = ""; // Reset input so same file can be selected again if needed
  };

  // Remove image by index (either uploaded or local new file)
  const handleRemoveImage = async (idx) => {
    if (
      !(await confirmDelete({
        title: "Remove image?",
        text: "This image will be removed from the carousel.",
        confirmText: "Yes, remove it",
      }))
    )
      return;

    const existingCount = (galleryHeroConfig?.backgroundUrls || []).length;
    // If idx < existingCount, it is an already-uploaded image, else it is a new local file
    if (idx < existingCount && bgFiles.length === 0) {
      // Remove from Firebase (already uploaded)
      await deleteGalleryHeroImage(idx);
    } else {
      // Remove from local files (not uploaded yet)
      setBgFiles((prev) => prev.filter((_, i) => i !== idx - existingCount));
    }

    setPreviewUrls((prev) => prev.filter((_, i) => i !== idx));
    if (slideIndex >= previewUrls.length - 1 && slideIndex > 0) {
      setSlideIndex(slideIndex - 1);
    }
  };

  // Save handler: updates text, uploads images, updates backgroundUrls
  const handleSave = async () => {
    if (
      !(await confirmDialog({
        title: "Save changes?",
        text: "Save Gallery Hero section changes?",
        confirmText: "Yes, save",
      }))
    )
      return;
    try {
      setSaving(true);

      // Update hero text
      await updateGalleryHeroConfig({
        heroText: localConfig.heroText,
        highlightText: localConfig.highlightText,
      });

      // Upload new images, if any
      if (bgFiles.length > 0) {
        await uploadGalleryHeroBackground(bgFiles);
      }

      successToast("Gallery Hero section updated.");
    } catch (err) {
      console.error(err);
      errorToast("Error saving Gallery Hero section.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10">
      <h2 className="text-3xl font-bold text-gray-800">
        Gallery Hero Section Admin Panel
      </h2>

      {/* Text Input */}
      <section className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-xl font-semibold text-gray-700">Hero Text</h3>
        <input
          name="heroText"
          value={localConfig.heroText}
          onChange={handleChange}
          placeholder="Hero Text (e.g., Discover)"
          className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-400"
        />
        <input
          name="highlightText"
          value={localConfig.highlightText}
          onChange={handleChange}
          placeholder="Highlight Text (e.g., Flavours of Vivaak)"
          className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-400"
        />
      </section>

      {/* Image Carousel Section */}
      <section className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-xl font-semibold text-gray-700">
          Background Images (Carousel)
        </h3>
        <MultiImageUploadField
          label=""
          aspect="wide"
          hint="Recommended size: 1920×1080px (16:9)"
          existingUrls={galleryHeroConfig?.backgroundUrls || []}
          newFiles={bgFiles}
          onAdd={(picked) => setBgFiles((prev) => [...prev, ...picked])}
          onRemove={async (idx, isExisting) => {
            if (isExisting) {
              if (
                await confirmDelete({
                  title: "Remove image?",
                  text: "This image will be removed from the carousel.",
                  confirmText: "Yes, remove it",
                })
              ) {
                await deleteGalleryHeroImage(idx);
              }
            } else {
              setBgFiles((prev) => prev.filter((_, i) => i !== idx));
            }
          }}
        />
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className={`px-6 py-3 rounded-lg transition font-medium ${
            saving
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0]"
          }`}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default GalleryHero;
